import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from '../../dialog/dialog.service';
import { InspectorService } from '../../inspector/inspector.service';
import { SettingService } from '../../setting/setting.service';
import { Inspection } from '../../shared/interfaces';
import { VendorService } from '../../vendor/vendor.service';
import { InspectionService } from '../inspection.service';

@Component({
  selector: 'app-send-email-inspection',
  templateUrl: './send.email.component.html',
})
export class InspectionSendEmailComponent implements OnInit {
  @ViewChild('sendEmailModal', { static: true }) public sendEmailModal: ModalDirective;

  statusAsText: () => ReturnType<InspectionService['statusAsText']>;

  confirmCallback: () => void;
  cancelCallback: () => void;
  inspection: Inspection;
  sendRemindersToInspectors = false;
  emails = [];
  selectedEmail = Emails.INSPECTION_DUE;

  constructor(
    public vendorService: VendorService,
    public inspectorService: InspectorService,
    public inspectionService: InspectionService,
    private toastr: ToastrService,
    private settingService: SettingService,
    private dialog: DialogService,
    private fb: UntypedFormBuilder,
  ) {
    this.statusAsText = () => inspectionService.statusAsText(this.inspection);
  }

  async ngOnInit() {
    [this.sendRemindersToInspectors] = await Promise.all([this.settingService.sendRemindersDirectlyToInspectors()]);
  }

  show(options: { confirmed?: () => void; canceled?: () => void; inspection: Inspection }) {
    this.confirmCallback = options.confirmed;
    this.cancelCallback = options.canceled;
    this.inspection = options.inspection;
    this.populateEmails();

    this.sendEmailModal.show();
  }

  populateEmails() {
    this.emails = this.availableEmails(this.inspection);
    this.selectedEmail = this.emails[0];
  }

  availableEmails(inspection) {
    const emails = [];

    if (this.showInspectionDue(inspection)) {
      emails.push(Emails.INSPECTION_DUE);
    }

    if (this.showAssigmentReminder(inspection)) {
      emails.push(Emails.ASSIGNMENT_REMINDER);
    }

    if (this.showRejected(inspection)) {
      emails.push(Emails.INSPECTION_REJECTED);
    }

    if (this.showInspectorAssigned(inspection)) {
      emails.push(Emails.ARU_CUSTOMER_BROKER_INSPECTION_ASSIGNMENT);
    }

    if (this.showInspectionReassigned(inspection)) {
      emails.push(Emails.INSPECTION_REASSIGNED_INSURED);
    }

    if (this.showUnsuccessfulContact(inspection)) {
      emails.push(Emails.UNSUCCESSFUL_CONTACT_INSURED_BROKER);
    }

    if (this.showFinalUnsuccessfulContact(inspection)) {
      emails.push(Emails.FINAL_UNSUCCESSFUL_CONTACT_INSURED_BROKER);
    }

    return emails;
  }

  async send() {
    switch (this.selectedEmail) {
      case Emails.INSPECTION_DUE:
        this.sendReminderDue();
        break;

      case Emails.ASSIGNMENT_REMINDER:
        this.sendReminderAssign();
        break;

      case Emails.INSPECTION_REJECTED:
        this.sendReminderRejected();
        break;

      case Emails.ARU_CUSTOMER_BROKER_INSPECTION_ASSIGNMENT:
        this.sendInspectorAssigned();
        break;

      case Emails.INSPECTION_REASSIGNED_INSURED:
        this.sendInspectionReassigned();
        break;

      case Emails.UNSUCCESSFUL_CONTACT_INSURED_BROKER:
        this.sendUnsuccessfulContact();
        break;

      case Emails.FINAL_UNSUCCESSFUL_CONTACT_INSURED_BROKER:
        this.sendFinalUnsuccessfulContact();
        break;
    }
    this.sendEmailModal.hide();
  }

  cancel() {
    this.sendEmailModal.hide();
    return !!this.cancelCallback && this.cancelCallback();
  }

  sendReminderDue() {
    if (this.sendRemindersToInspectors) {
      this.dialog.confirm().show({
        message: 'Send reminder to inspector?',
        confirmed: async () => {
          await this.inspectionService.sendReminderToInspectorDue(this.inspection.id);
          this.toastr.success('Message sent!');
          this.confirmCallback && this.confirmCallback();
        },
      });
    } else {
      this.dialog.confirm().show({
        message: 'Send reminder to vendor?',
        confirmed: async () => {
          await this.inspectionService.sendReminderVendorDue(this.inspection.id);
          this.toastr.success('Message sent!');
          this.confirmCallback && this.confirmCallback();
        },
      });
    }
  }

  sendReminderAssign() {
    this.dialog.confirm().show({
      message: 'Send reminder to vendor?',
      confirmed: async () => {
        await this.inspectionService.sendReminderVendorAssign(this.inspection.id);
        this.toastr.success('Message sent!');
      },
    });
  }

  sendInspectionReassigned() {
    this.dialog.confirm().show({
      message: 'Send email to insured?',
      confirmed: async () => {
        await this.inspectionService.sendInspectionReassignedEmail(this.inspection.id);
        this.toastr.success('Message sent!');
      },
    });
  }

  sendUnsuccessfulContact() {
    this.dialog.confirm().show({
      message: 'Send unsuccessful contact email to insured and broker?',
      confirmed: async () => {
        await this.inspectionService.sendUnsuccessfulContactEmail(this.inspection.id);
        this.toastr.success('Message sent!');
      },
    });
  }

  sendFinalUnsuccessfulContact() {
    this.dialog.confirm().show({
      message: 'Send final unsuccessful contact email to insured and broker?',
      confirmed: async () => {
        await this.inspectionService.sendFinalUnsuccessfulContactEmail(this.inspection.id);
        this.toastr.success('Message sent!');
      },
    });
  }

  sendReminderRejected() {
    if (this.sendRemindersToInspectors) {
      this.dialog.confirm().show({
        message: 'Send reminder to Inspector?',
        confirmed: async () => {
          await this.inspectionService.sendReminderToInspectorRejected(this.inspection.id);
          this.toastr.success('Reminder has been sent!');
        },
      });
    } else {
      this.dialog.confirm().show({
        message: 'Send reminder to Vendor?',
        confirmed: async () => {
          await this.inspectionService.sendReminderVendorRejected(this.inspection.id);
          this.toastr.success('Reminder has been sent!');
        },
      });
    }
  }

  async sendInspectorAssigned() {
    const lastEmailSentDate = await this.getLastEmailSentDate();
    const daysAgo = lastEmailSentDate ? this.calculateDaysAgo(lastEmailSentDate) : null;

    const appendMessageForDaysAgo = days => {
      if (days === null) return '';
      if (days === 0) return `<p>This email was already sent <b>today</b>.</p>`;
      if (days === 1) return `<p>This email was already sent <b>yesterday</b>.</p>`;
      return `<p>This email was already sent <b>${days} days</b> ago.</p>`;
    };

    const message =
      `<p>Notify <b>Broker</b> and <b>Customer</b> that the inspector has been assigned?</p>` +
      appendMessageForDaysAgo(daysAgo);

    this.dialog.confirm().show({
      messageHtml: message,
      confirmed: async () => {
        await this.inspectionService.notifyBrokerAndCustomerInspectorAssigned(this.inspection.id);
        this.inspection.changes = await this.inspectionService.changes(this.inspection.id);
        this.toastr.success('Message sent to Broker and Customer!');
      },
    });
  }

  async getLastEmailSentDate(): Promise<Date | null> {
    const changes = await this.inspectionService.changes(this.inspection.id);
    const emailSentEntries = changes
      .filter(
        change =>
          change.changes.EMAIL_SENT &&
          change.changes.EMAIL_SENT.template === 'ARU_CUSTOMER_BROKER_INSPECTION_ASSIGNMENT',
      )
      .map(change => new Date(change.date));

    if (emailSentEntries.length === 0) return null;

    emailSentEntries.sort((a, b) => b.getTime() - a.getTime());
    return emailSentEntries[0];
  }

  calculateDaysAgo(date: Date): number {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const sentDate = new Date(date);
    sentDate.setHours(0, 0, 0, 0);

    const timeDiff = currentDate.getTime() - sentDate.getTime();
    return Math.floor(timeDiff / (1000 * 3600 * 24));
  }

  showAssigmentReminder(inspection) {
    // "both overdue as well as cards that are due within the next 7 days and have no inspector assigned"
    const overdue = inspection.dueDate && new Date(inspection.dueDate) < new Date();
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);

    return (
      this.inspectionService.statusAsText(inspection) === 'Assignment made' &&
      (overdue ||
        (!inspection.inspector && inspection.dueDate && new Date(inspection.dueDate).getTime() < nextWeek.getTime()
          ? true
          : false))
    );
  }

  showInspectionDue(inspection) {
    return !inspection.lastSynced && this.inspectionService.statusAsText(inspection) === 'Due completion';
  }

  showRejected(inspection) {
    return this.inspectionService.statusAsText(inspection) === 'Rejected';
  }

  showInspectorAssigned(inspection) {
    const assignmentMade =
      this.inspectionService.statusAsText(inspection) === 'Assignment made' &&
      inspection.inspector &&
      inspection.dueDate;
    const dueCompletion = this.inspectionService.statusAsText(inspection) === 'Due completion' && inspection.inspector;
    return assignmentMade || dueCompletion;
  }

  showInspectionReassigned(inspection) {
    const assignmentMade =
      this.inspectionService.statusAsText(inspection) === 'Assignment made' &&
      inspection.inspector &&
      inspection.dueDate;
    const dueCompletion = this.inspectionService.statusAsText(inspection) === 'Due completion' && inspection.inspector;
    return assignmentMade || dueCompletion;
  }

  showUnsuccessfulContact(inspection) {
    return this.inspectionService.statusAsText(inspection) === 'Due completion' && inspection.inspector;
  }

  showFinalUnsuccessfulContact(inspection) {
    return this.inspectionService.statusAsText(inspection) === 'Due completion' && inspection.inspector;
  }
}

enum Emails {
  INSPECTION_DUE = 'Inspection Due',
  INSPECTION_REJECTED = 'Inspection Rejected',
  ASSIGNMENT_REMINDER = 'Assignment Reminder',
  ARU_CUSTOMER_BROKER_INSPECTION_ASSIGNMENT = 'Inspector Assigned',
  INSPECTION_REASSIGNED_INSURED = 'Inspection reassigned (to insured)',
  UNSUCCESSFUL_CONTACT_INSURED_BROKER = 'Unsuccessful contact',
  FINAL_UNSUCCESSFUL_CONTACT_INSURED_BROKER = 'Unsuccessful contact - Final',
}
